import React, { useEffect, useState, useCallback } from 'react';
import { usePage } from '../../hooks/usePage';
import { usersApi } from '../../api/users';
import { PayIQUser, PaginatedResponse } from '../../types';
import { Card } from '../../components/ui/Card';
import { Table } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { Pagination } from '../../components/ui/Pagination';
import { UserDetail } from './UserDetail';

const tierColors: Record<string, 'neutral' | 'info' | 'success' | 'premium'> = {
  tier_0: 'neutral',
  tier_1: 'info',
  tier_2: 'success',
  tier_3: 'premium',
};

const statusColors: Record<string, 'success' | 'danger' | 'neutral'> = {
  active: 'success',
  suspended: 'danger',
  deactivated: 'neutral',
};

const formatTier = (tier: string) =>
  tier.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-NG', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const Users: React.FC = () => {
  const [search, setSearch] = useState('');

  usePage('Users', {
    placeholder: 'Search by phone, email, or name…',
    onSearch: (val) => setSearch(val),
  });

  const [data, setData] = useState<PaginatedResponse<PayIQUser> | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [tierFilter, setTierFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const result = await usersApi.getUsers({
        search: search || undefined,
        tier: tierFilter || undefined,
        status: statusFilter || undefined,
        page,
        limit: 20,
      });
      setData(result);
    } catch (err) {
      // error handling
    } finally {
      setLoading(false);
    }
  }, [search, tierFilter, statusFilter, page]);

  useEffect(() => {
    setPage(1);
  }, [search, tierFilter, statusFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      void fetchUsers();
    }, 250);
    return () => clearTimeout(timer);
  }, [fetchUsers]);

  const filterSelectStyle: React.CSSProperties = {
    height: 38,
    padding: '0 32px 0 12px',
    fontSize: 13,
    fontFamily: 'var(--font-family)',
    color: 'var(--neutral-700)',
    background: 'var(--neutral-100)',
    border: '1px solid var(--neutral-300)',
    borderRadius: 'var(--radius-md)',
    outline: 'none',
    appearance: 'none',
    backgroundImage:
      'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\' fill=\'none\'%3E%3Cpath d=\'M3 4.5L6 7.5L9 4.5\' stroke=\'%23566472\' stroke-width=\'1.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 10px center',
    cursor: 'pointer',
  };

  return (
    <>
      <Card padding={20}>
        <div
          style={{
            display: 'flex',
            gap: 12,
            marginBottom: 20,
            flexWrap: 'wrap',
          }}
        >
          <select
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
            style={filterSelectStyle}
          >
            <option value="">All Tiers</option>
            <option value="tier_0">Tier 0</option>
            <option value="tier_1">Tier 1</option>
            <option value="tier_2">Tier 2</option>
            <option value="tier_3">Tier 3</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={filterSelectStyle}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="deactivated">Deactivated</option>
          </select>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner size={24} />
          </div>
        ) : data ? (
          <>
            <Table
              data={data.items}
              onRowClick={(user) => setSelectedUserId(user.id)}
              columns={[
                {
                  key: 'name',
                  header: 'Name',
                  render: (u) => (
                    <div>
                      <div style={{ fontWeight: 600, color: 'var(--neutral-800)' }}>
                        {u.firstName || u.lastName
                          ? `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim()
                          : 'Unnamed User'}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                        {u.phoneNumber}
                      </div>
                    </div>
                  ),
                },
                {
                  key: 'tier',
                  header: 'Tier',
                  render: (u) => (
                    <Badge variant={tierColors[u.tier]}>{formatTier(u.tier)}</Badge>
                  ),
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (u) => (
                    <Badge variant={statusColors[u.status]}>{u.status}</Badge>
                  ),
                },
                {
                  key: 'premium',
                  header: 'Premium',
                  render: (u) =>
                    u.isPremium ? (
                      <Badge variant="premium">Premium</Badge>
                    ) : (
                      <span style={{ color: 'var(--neutral-400)', fontSize: 13 }}>—</span>
                    ),
                },
                {
                  key: 'account',
                  header: 'Account No.',
                  render: (u) => (
                    <span
                      style={{
                        fontFamily: 'monospace',
                        fontSize: 13,
                        color: 'var(--neutral-700)',
                      }}
                    >
                      {u.accountNumber ?? '—'}
                    </span>
                  ),
                },
                {
                  key: 'created',
                  header: 'Joined',
                  render: (u) => (
                    <span style={{ fontSize: 13, color: 'var(--neutral-600)' }}>
                      {formatDate(u.createdAt)}
                    </span>
                  ),
                },
              ]}
              emptyMessage={
                search || tierFilter || statusFilter
                  ? 'No users match your filters'
                  : 'No users yet'
              }
            />
            <Pagination
              page={data.meta.page}
              totalPages={data.meta.totalPages}
              total={data.meta.total}
              onPageChange={setPage}
            />
          </>
        ) : null}
      </Card>

      <UserDetail
        userId={selectedUserId}
        isOpen={selectedUserId !== null}
        onClose={() => setSelectedUserId(null)}
        onUpdate={() => void fetchUsers()}
      />
    </>
  );
};